import { useEffect, useRef, useState } from 'react';
import { trpc } from '@org/clients/school';
import { graphTrpc } from '@org/clients/graph';
import { setSentryUser, clearSentryUser } from '@org/utils-client';
import { useAuth } from './auth-context';
import { TermsAndConditionsDialog } from '../security/terms-and-conditions-dialog';

type VerificationStatus =
  | 'unknown'
  | 'joined'
  | 'already_joined'
  | 'awaiting-terms'
  | 'invited'
  | 'new'
  | 'disabled';

export const useAutoUserVerification = () => {
  const utils = trpc.useUtils();
  const { isLoading: authLoading, isLoggedIn, user, refreshAccessToken, loginSilent } = useAuth();
  const hasAttemptedVerification = useRef(false);

  useEffect(() => {
    if (isLoggedIn && user?.userId) {
      setSentryUser({ id: user.userId, email: user.email });
    } else if (!isLoggedIn) {
      clearSentryUser();
    }
  }, [isLoggedIn, user?.userId, user?.email]);
  const [shouldShowTerms, setShouldShowTerms] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>('unknown');
  const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);

  // Refresh the Auth0 access token to get updated roles/permissions
  // This is needed because Auth0 assigns roles during verification,
  // but the current token doesn't contain those roles yet
  const refreshTokenWithNewRoles = async () => {
    try {
      const newToken = await refreshAccessToken();
      return true;
    } catch (error) {
      // Token refresh failed - this typically happens when:
      // 1. consent_required: SDK fell back to iframe auth which needs user interaction
      // 2. login_required: Session expired
      // 3. Third-party cookies blocked
      console.warn('[Auth] Token refresh failed:', error);
      return false;
    }
  };

  const {
    mutate: triggerVerification,
    isPending: isVerifying,
    isSuccess: hasVerified,
  } = graphTrpc.students.verifyUser.useMutation({
    onSuccess: async (result) => {
      const status = (result?.status ?? 'unknown') as VerificationStatus;
      setVerificationStatus(status);

      if (status === 'awaiting-terms') {
        setShouldShowTerms(true);
        return;
      }

      // For joined/already_joined, refresh token to get new roles from Auth0
      // then invalidate permissions cache to ensure UI reflects new permissions
      if (status === 'joined' || status === 'already_joined') {
        await refreshTokenWithNewRoles();
        await utils.auth.getPermissions.invalidate();
      }
    },
    onError: (error) => {
      console.error('User verification failed', error);
    },
  });

  const { mutateAsync: acceptTermsAsync } = graphTrpc.students.acceptTerms.useMutation();

  const acknowledgeTerms = async () => {
    setIsAcceptingTerms(true);
    try {
      const result = await acceptTermsAsync();
      const status = (result?.status ?? 'unknown') as VerificationStatus;
      setVerificationStatus(status);

      // Close modal immediately regardless of what happens next
      setShouldShowTerms(false);

      // For joined/already_joined, we need to get a fresh token with the new roles
      // Try silent refresh first, if that fails, fall back to silent re-login
      if (status === 'joined') {
        const refreshed = await refreshTokenWithNewRoles();
        
        if (refreshed) {
          // Token refresh succeeded - invalidate cache and continue
          await utils.auth.getPermissions.invalidate();
        } else {
          // Token refresh failed - this can happen if:
          // 1. User doesn't have a refresh token yet (logged in before "Allow Offline Access" was enabled)
          // 2. Refresh token expired
          // Fall back to silent re-login through Auth0
          await loginSilent();
          return; // loginSilent() redirects, so we won't reach here
        }
      }
    } catch (error) {
      console.error('Failed to accept terms', error);
      // Still close modal on error - user can refresh and try again
      setShouldShowTerms(false);
    } finally {
      setIsAcceptingTerms(false);
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      hasAttemptedVerification.current = false;
      setShouldShowTerms(false);
      return;
    }

    if (!authLoading && isLoggedIn && !isVerifying && !hasVerified) {
      if (hasAttemptedVerification.current) {
        return;
      }

      hasAttemptedVerification.current = true;
      triggerVerification();
    }
  }, [authLoading, isLoggedIn, isVerifying, hasVerified, triggerVerification]);

  return {
    shouldShowTerms,
    acknowledgeTerms,
    verificationStatus,
    isVerifying,
    hasVerified,
    isAcceptingTerms,
  };
};

export const AuthBootstrapper = () => {
  const { shouldShowTerms, acknowledgeTerms, isAcceptingTerms } = useAutoUserVerification();

  return (
    <TermsAndConditionsDialog
      open={shouldShowTerms}
      onAccept={acknowledgeTerms}
      supportEmail="support@english2empower.org"
      isAccepting={isAcceptingTerms}
    />
  );
};
